import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffEditor from './StaffEditor';
import PersonnelPdfButton from './PersonnelPdfButton';
import { compactTime, dayLongLabel } from './AgendaResponsiveView';
import { clientDisplayName } from '../../utils/clientName';
import useSwipeBack from '../../hooks/useSwipeBack';

function uniqueByName(items, fallback = []) {
  const names = new Set();
  const result = [];
  for (const item of [...items, ...fallback]) {
    const name = typeof item === 'string' ? item : item?.name || item?.first_name;
    if (!name || names.has(name)) continue;
    names.add(name);
    result.push(name);
  }
  return result;
}

function placeOptions(lugares = [], currentPlace = '') {
  const options = [];
  for (const lugar of lugares) {
    if (lugar?.name) options.push(lugar.name);
    for (const espacio of lugar?.espacios || []) {
      options.push(`${lugar.name} - ${espacio.name}`);
    }
  }
  return uniqueByName(options, currentPlace ? [currentPlace] : []);
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

function clampMinuteStep(value) {
  const minute = Math.min(59, Math.max(0, Number(value) || 0));
  return Math.min(55, Math.round(minute / 5) * 5);
}

function normalizeTimeText(value) {
  const raw = String(value || '').trim().replace('.', ':');
  if (!raw) return '';
  if (/^\d{1,2}$/.test(raw)) return `${pad2(Math.min(23, Math.max(0, Number(raw))))}:00`;
  if (/^\d{3}$/.test(raw)) return `${pad2(Math.min(23, Math.max(0, Number(raw.slice(0, 1)))))}:${pad2(clampMinuteStep(raw.slice(1)))}`;
  if (/^\d{4}$/.test(raw)) return `${pad2(Math.min(23, Math.max(0, Number(raw.slice(0, 2)))))}:${pad2(clampMinuteStep(raw.slice(2)))}`;
  const match = raw.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return raw;
  return `${pad2(Math.min(23, Math.max(0, Number(match[1]))))}:${pad2(clampMinuteStep(match[2]))}`;
}

function timeParts(value, fallback = '08:00') {
  const normalized = normalizeTimeText(value) || fallback;
  const [hour, minute] = normalized.split(':');
  return {
    hour: pad2(Math.min(23, Math.max(0, Number(hour) || 0))),
    minute: pad2(clampMinuteStep(minute)),
  };
}

const TimeWheelColumn = forwardRef(function TimeWheelColumn({ label, values, value, onChange }, externalRef) {
  const ref = useRef(null);
  const scrollTimer = useRef(null);
  const snappingRef = useRef(false);
  const itemHeight = 42;
  const copyCount = 7;
  const middleCopy = Math.floor(copyCount / 2);
  const wheelValues = useMemo(() => Array.from({ length: copyCount }, () => values).flat(), [values]);

  function centeredIndexFor(scrollTop, clientHeight) {
    const centerOffset = scrollTop + (clientHeight / 2) - (itemHeight / 2);
    return Math.round(centerOffset / itemHeight);
  }

  function valueFromIndex(index) {
    return values[((index % values.length) + values.length) % values.length];
  }

  function targetTopForIndex(index, el = ref.current) {
    if (!el) return 0;
    return (index * itemHeight) - ((el.clientHeight - itemHeight) / 2);
  }

  function scrollToValue(nextValue, behavior = 'auto') {
    const el = ref.current;
    const index = values.indexOf(nextValue);
    if (!el || index < 0) return;
    const targetIndex = (values.length * middleCopy) + index;
    snappingRef.current = true;
    el.scrollTo({ top: targetTopForIndex(targetIndex, el), behavior });
    window.setTimeout(() => {
      snappingRef.current = false;
    }, behavior === 'smooth' ? 240 : 40);
  }

  function readCenteredValue() {
    const el = ref.current;
    if (!el) return value;
    const index = centeredIndexFor(el.scrollTop, el.clientHeight);
    return valueFromIndex(index) || value;
  }

  function snapToCenteredValue(behavior = 'auto') {
    const el = ref.current;
    if (!el) return value;
    const index = centeredIndexFor(el.scrollTop, el.clientHeight);
    const next = valueFromIndex(index) || value;
    snappingRef.current = true;
    el.scrollTo({ top: targetTopForIndex(index, el), behavior });
    window.setTimeout(() => {
      snappingRef.current = false;
    }, behavior === 'smooth' ? 220 : 40);
    onChange(next);
    return next;
  }

  useImperativeHandle(externalRef, () => ({
    getCenteredValue: readCenteredValue,
    snapToCenter: snapToCenteredValue,
  }));

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => scrollToValue(value, 'auto'));
    return () => window.cancelAnimationFrame(frame);
  }, [values]);

  function queueSnap(delay = 90) {
    if (scrollTimer.current) window.clearTimeout(scrollTimer.current);
    scrollTimer.current = window.setTimeout(() => {
      if (!snappingRef.current) snapToCenteredValue('smooth');
    }, delay);
  }

  function handleScroll(event) {
    const index = centeredIndexFor(event.currentTarget.scrollTop, event.currentTarget.clientHeight);
    const next = valueFromIndex(index);
    if (next && next !== value) onChange(next);
    queueSnap(110);
  }

  return (
    <div className="agm-time-wheel">
      <span>{label}</span>
      <div
        className="agm-time-wheel-list"
        ref={ref}
        onScroll={handleScroll}
        onTouchEnd={() => queueSnap(20)}
        onPointerUp={() => queueSnap(20)}
        onPointerCancel={() => queueSnap(20)}
        onBlur={() => queueSnap(20)}
      >
        {wheelValues.map((item, index) => (
          <button
            key={`${item}-${index}`}
            type="button"
            tabIndex={-1}
            className={item === value ? 'active' : ''}
            onClick={event => {
              event.preventDefault();
            }}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
});

function TimePickerSheet({ picker, onChange, onCancel, onConfirm }) {
  const hours = useMemo(() => Array.from({ length: 24 }, (_, index) => pad2(index)), []);
  const minutes = useMemo(() => Array.from({ length: 12 }, (_, index) => pad2(index * 5)), []);
  const current = `${picker.hour}:${picker.minute}`;
  const hourWheelRef = useRef(null);
  const minuteWheelRef = useRef(null);

  return (
    <div className="agm-time-picker-backdrop" role="presentation" onClick={onCancel}>
      <section
        className="agm-time-picker"
        role="dialog"
        aria-modal="true"
        aria-label={picker.title}
        onClick={event => event.stopPropagation()}
        onTouchStart={event => event.stopPropagation()}
        onTouchEnd={event => event.stopPropagation()}
      >
        <header>
          <p>{picker.title}</p>
          <strong>{current}</strong>
        </header>
        <div className="agm-time-wheel-shell" aria-label="Selector de hora">
          <TimeWheelColumn ref={hourWheelRef} label="HH" values={hours} value={picker.hour} onChange={hour => onChange({ hour })} />
          <span className="agm-time-separator">:</span>
          <TimeWheelColumn ref={minuteWheelRef} label="MM" values={minutes} value={picker.minute} onChange={minute => onChange({ minute })} />
        </div>
        <form
          className="agm-time-picker-actions"
          onSubmit={event => {
            event.preventDefault();
            event.stopPropagation();
            const hour = hourWheelRef.current?.snapToCenter?.('auto') || picker.hour;
            const minute = minuteWheelRef.current?.snapToCenter?.('auto') || picker.minute;
            onConfirm({ hour, minute });
          }}
        >
          <button
            type="button"
            className="agm-time-cancel"
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="agm-time-confirm"
          >
            Confirmar
          </button>
        </form>
      </section>
    </div>
  );
}

export default function AgendaMobileEventEditor({
  event,
  isCreating,
  canEdit,
  canEditAssignments,
  personnel,
  lugares,
  clients,
  salesRefs,
  assignmentModel,
  assignmentModelError,
  retryAssignmentModel,
  canViewEquipment,
  onCancel,
  onSave,
}) {
  const navigate = useNavigate();
  const staffTabGroup = useRef({});
  const swipeBack = useSwipeBack(onCancel);
  const titleRef = useRef(null);

  // El textarea no se encoge solo: se ajusta a su contenido en cada cambio para
  // que un título de una línea no reserve el alto de dos.
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  });

  const [form, setForm] = useState({
    name: event?.name || '',
    event_date: event?.event_date?.slice(0, 10) || '',
    odoo_code: event?.odoo_code || '',
    client: event?.client || '',
    sales_referent: event?.sales_referent || '',
    place: event?.place || '',
    status: event?.status || 'confirmado',
    setup_start: compactTime(event?.setup_start),
    start_time: compactTime(event?.start_time),
    end_time: compactTime(event?.end_time),
    notes: event?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [timePicker, setTimePicker] = useState(null);
  const clientsList = useMemo(
    () => uniqueByName(clients.map(c => ({ name: clientDisplayName(c) })), form.client ? [form.client] : []),
    [clients, form.client]
  );
  const refsList = useMemo(() => uniqueByName(salesRefs?.length ? salesRefs : personnel, form.sales_referent ? [form.sales_referent] : []), [salesRefs, personnel, form.sales_referent]);
  const places = useMemo(() => placeOptions(lugares, form.place), [lugares, form.place]);
  const disabled = !canEdit || saving;
  const orderLabel = form.odoo_code || (isCreating ? 'Nuevo evento' : `#${event?.id}`);

  function setField(key) {
    return event => setForm(prev => ({ ...prev, [key]: event.target.value }));
  }

  function openTimePicker(field, title, fallback) {
    if (disabled) return;
    const parts = timeParts(form[field], fallback);
    setTimePicker({ field, title, ...parts });
  }

  function updateTimePicker(patch) {
    setTimePicker(prev => prev ? { ...prev, ...patch } : prev);
  }

  function confirmTimePicker(centeredParts = {}) {
    if (!timePicker) return;
    const hour = centeredParts.hour || timePicker.hour;
    const minute = centeredParts.minute || timePicker.minute;
    setForm(prev => ({ ...prev, [timePicker.field]: `${hour}:${minute}` }));
    setTimePicker(null);
  }

  async function submit() {
    setSaving(true);
    setError('');
    try {
      if (!form.name.trim()) throw new Error('El nombre es obligatorio.');
      if (!form.event_date) throw new Error('La fecha es obligatoria.');
      await onSave({
        ...form,
        setup_start: normalizeTimeText(form.setup_start),
        start_time: normalizeTimeText(form.start_time),
        end_time: normalizeTimeText(form.end_time),
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="agm-editor-shell" {...swipeBack}>
      <header className="agm-editor-header">
        <div className="agm-editor-top">
          <button type="button" className="agm-back-btn" onClick={onCancel}>‹</button>
          <span className="agm-editor-top-spacer" aria-hidden="true" />
          {canEdit && <button type="button" className="agm-save-btn" onClick={submit} disabled={saving}>{saving ? 'Guardando' : 'Guardar'}</button>}
        </div>
        <div className="agm-editor-title-block">
          <p>{orderLabel} · {form.event_date ? dayLongLabel(form.event_date) : 'Sin fecha'}</p>
          {/* rows=1 más autoajuste: con rows=2 el campo reservaba siempre dos
              renglones, y un título de una línea dejaba un hueco muerto debajo. */}
          <textarea
            ref={titleRef}
            className="agm-editor-title-input"
            value={form.name}
            onChange={setField('name')}
            disabled={disabled}
            placeholder="Nombre del evento"
            rows={1}
          />
          {/* El lugar es lo primero que se pregunta y estaba enterrado en el
              desplegable de datos. Además ocupa el hueco que dejaba el campo
              del título al reservar dos renglones. */}
          <span className="agm-editor-title-place">
            {form.place || 'Sin lugar'}
          </span>
        </div>
      </header>

      {/* Arriba y destacado: es la acción que más se usa en esta pantalla.
          El navigate va con replace: reemplaza la entrada de historial del
          editor, así un solo atrás desde equipos vuelve a la agenda en vez de
          gastarse en un paso mudo. */}
      {canViewEquipment && !isCreating && (
        <button
          type="button"
          className="agm-editor-equipment-action"
          onClick={() => navigate(`/agenda/eventos/${event.id}/equipos`, { replace: true })}
        >
          <span>Ver equipos</span>
          <b>›</b>
        </button>
      )}


      <section className="agm-editor-card">
        <h2>Horarios</h2>
        <div className="agm-time-grid">
          <label className="arm">
            <span>Armado</span>
            <button type="button" className="agm-time-button" disabled={disabled} onClick={() => openTimePicker('setup_start', 'Hora armado', '08:00')}>
              {normalizeTimeText(form.setup_start) || '--:--'}
            </button>
          </label>
          <label className="op">
            <span>Comienzo</span>
            <button type="button" className="agm-time-button" disabled={disabled} onClick={() => openTimePicker('start_time', 'Hora comienzo', '20:00')}>
              {normalizeTimeText(form.start_time) || '--:--'}
            </button>
          </label>
          <label className="des">
            <span>Fin</span>
            <button type="button" className="agm-time-button" disabled={disabled} onClick={() => openTimePicker('end_time', 'Hora fin', '02:00')}>
              {normalizeTimeText(form.end_time) || '--:--'}
            </button>
          </label>
        </div>
      </section>

      <section className="agm-editor-card agm-editor-assignments">
        <div className="agm-editor-card-head">
          <h2>Asignaciones</h2>
          {!isCreating && <PersonnelPdfButton entityType="event" entityId={event.id} />}
        </div>
        {isCreating ? (
          <p className="agm-editor-empty-copy">Guardá el evento para cargar asignaciones.</p>
        ) : (
          <StaffEditor
            eventId={event.id}
            initialAssignments={event.staff_assignments || []}
            initialSections={event.staff_sections || []}
            assignmentModel={assignmentModel}
            assignmentModelError={assignmentModelError}
            retryAssignmentModel={retryAssignmentModel}
            personnel={personnel || []}
            canEdit={canEditAssignments}
            tabGroup={staffTabGroup}
            tabIndexStart={20}
            entityDate={form.event_date}
            startTime={form.start_time}
            endTime={form.end_time}
            touchPicker
          />
        )}
      </section>

      {/* Plegado por defecto: en el uso diario se entra a ver horarios y
          asignaciones, no a editar cliente o notas. */}
      <details className="agm-editor-details">
        <summary>
          <span>Datos del evento</span>
          <small>Orden, estado, cliente, lugar, notas</small>
        </summary>
        <section className="agm-editor-card agm-editor-info">
        <div className="agm-editor-line agm-editor-line--two">
          <label>
            <span># orden</span>
            <input value={form.odoo_code} onChange={setField('odoo_code')} disabled={disabled} placeholder="S00000" />
          </label>
          <label>
            <span>Estado</span>
            <select value={form.status} onChange={setField('status')} disabled={disabled}>
              <option value="confirmado">Confirmado</option>
              <option value="posible">Posible</option>
              <option value="cancelado">Cancelado</option>
              <option value="archivado">Archivado</option>
            </select>
          </label>
        </div>

        <div className="agm-editor-line agm-editor-line--two">
          <label>
            <span>Cliente</span>
            <input value={form.client} onChange={setField('client')} list="agm-clients" disabled={disabled} placeholder="Cliente" />
            <datalist id="agm-clients">{clientsList.map(name => <option key={name} value={name} />)}</datalist>
          </label>
          <label>
            <span>Referente</span>
            <input value={form.sales_referent} onChange={setField('sales_referent')} list="agm-sales-refs" disabled={disabled} placeholder="Referente" />
            <datalist id="agm-sales-refs">{refsList.map(name => <option key={name} value={name} />)}</datalist>
          </label>
        </div>

        <label>
          <span>Lugar</span>
          <input value={form.place} onChange={setField('place')} list="agm-places" disabled={disabled} placeholder="Lugar" />
          <datalist id="agm-places">{places.map(name => <option key={name} value={name} />)}</datalist>
        </label>

        <label>
          <span>Notas</span>
          <textarea value={form.notes} onChange={setField('notes')} disabled={disabled} placeholder="Notas operativas..." />
        </label>

        </section>
      </details>

      {error && <p className="agm-editor-error">{error}</p>}
      {timePicker && (
        <TimePickerSheet
          picker={timePicker}
          onChange={updateTimePicker}
          onCancel={() => setTimePicker(null)}
          onConfirm={confirmTimePicker}
        />
      )}
    </main>
  );
}
